import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import emailNotificationService from '../../../services/emailNotificationService';

const AutomatedEmailTriggers = () => {
  const [loading, setLoading] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    // Load last run stats from localStorage
    const savedStats = localStorage.getItem('emailTriggerStats');
    if (savedStats) {
      const parsed = JSON.parse(savedStats);
      setLastRun(parsed?.lastRun);
      setStats(parsed?.stats);
    }
  }, []);

  const handleRunDeadlineAlerts = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const result = await emailNotificationService?.checkAndSendDeadlineAlerts();
      
      const now = new Date()?.toISOString();
      const newStats = {
        lastRun: now,
        stats: {
          deadlineAlerts: result?.sent || 0,
          recipients: result?.recipients || 0
        }
      };

      setLastRun(now);
      setStats(newStats?.stats);
      localStorage.setItem('emailTriggerStats', JSON.stringify(newStats));

      setSuccess(`Successfully sent ${result?.sent || 0} deadline alerts to ${result?.recipients || 0} recipients`);
    } catch (err) {
      setError(err?.message || 'Failed to send deadline alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleRunViolationReports = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const result = await emailNotificationService?.sendScheduledViolationReports();
      
      const now = new Date()?.toISOString();
      const newStats = {
        lastRun: now,
        stats: {
          ...stats,
          violationReports: result?.processed || 0
        }
      };

      setLastRun(now);
      setStats(newStats?.stats);
      localStorage.setItem('emailTriggerStats', JSON.stringify(newStats));

      setSuccess(`Successfully processed ${result?.processed || 0} scheduled violation reports`);
    } catch (err) {
      setError(err?.message || 'Failed to send violation reports');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date?.toLocaleString('en-PG', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Zap" size={20} color="var(--color-primary)" />
          Automated Email Triggers
        </h3>
        {lastRun && (
          <span className="text-xs text-muted-foreground">
            Last run: {formatTimestamp(lastRun)}
          </span>
        )}
      </div>

      {error && (
        <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-4 flex items-start gap-2">
          <Icon name="AlertCircle" size={18} className="mt-0.5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-success/10 border border-success text-success px-4 py-3 rounded-md mb-4 flex items-start gap-2">
          <Icon name="CheckCircle" size={18} className="mt-0.5" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      <div className="space-y-4">
        {/* Deadline Alerts */}
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h4 className="font-medium text-foreground mb-1 flex items-center gap-2">
                <Icon name="Clock" size={16} color="var(--color-warning)" />
                Compliance Deadline Alerts
              </h4>
              <p className="text-xs text-muted-foreground">
                Automatically checks for upcoming compliance deadlines (within 7 days) and sends email alerts to compliance officers and payroll managers.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRunDeadlineAlerts}
              disabled={loading}
              iconName="Play"
            >
              Run Now
            </Button>
          </div>
          {stats?.deadlineAlerts !== undefined && (
            <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2">
              <span>• {stats?.deadlineAlerts} alerts sent</span>
              <span>• {stats?.recipients} recipients</span>
            </div>
          )}
        </div>

        {/* Violation Reports */}
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h4 className="font-medium text-foreground mb-1 flex items-center gap-2">
                <Icon name="AlertTriangle" size={16} color="var(--color-error)" />
                Scheduled Violation Reports
              </h4>
              <p className="text-xs text-muted-foreground">
                Processes all active scheduled deliveries and sends violation reports to configured recipients based on their delivery frequency.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRunViolationReports}
              disabled={loading}
              iconName="Play"
            >
              Run Now
            </Button>
          </div>
          {stats?.violationReports !== undefined && (
            <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2">
              <span>• {stats?.violationReports} reports processed</span>
            </div>
          )}
        </div>

        {/* Info Panel */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} color="var(--color-primary)" className="mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-foreground mb-1">Automation Schedule</h4>
              <p className="text-xs text-muted-foreground mb-2">
                In production, these triggers should be automated using a cron job or scheduled task:
              </p>
              <ul className="text-xs text-muted-foreground space-y-1 ml-4">
                <li>• <strong>Deadline Alerts:</strong> Run daily at 9:00 AM</li>
                <li>• <strong>Violation Reports:</strong> Run based on each schedule's frequency</li>
              </ul>
              <p className="text-xs text-muted-foreground mt-2">
                For now, use the "Run Now" buttons to manually trigger email notifications for testing and immediate needs.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomatedEmailTriggers;